"use client";

import { useState, FormEvent, useRef, ChangeEvent } from "react";
import { FiChevronDown, FiChevronUp, FiSend, FiCheckCircle, FiImage, FiTrash2 } from "react-icons/fi";

interface CustomRequestFormProps {
  onClose: () => void;
}

export default function CustomRequestForm({ onClose }: CustomRequestFormProps) {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  
  // États du formulaire - Informations de contact d'abord
  const [contactInfo, setContactInfo] = useState({
    name: "",
    email: "",
    phone: "",
    prestationAddress: "",
    billingAddress: "",
    sameAddress: true
  });
  
  // Puis informations du projet
  const [projectType, setProjectType] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [budget, setBudget] = useState("");
  const [timeframe, setTimeframe] = useState("");
  
  // Gestion des images
  const [images, setImages] = useState<string[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Gérer la copie de l'adresse
  const handleSameAddressChange = (checked: boolean) => {
    setContactInfo(prev => ({ ...prev, sameAddress: checked }));
    if (checked) {
      setContactInfo(prev => ({ ...prev, billingAddress: "" }));
    }
  };

  const handleImageUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    
    Array.from(files).forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        setError("L'image doit faire moins de 5MB");
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setImages(prev => [...prev, result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!projectType || !description || !contactInfo.name || !contactInfo.phone || !contactInfo.prestationAddress) {
      setError("Veuillez remplir tous les champs obligatoires (*)");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const customRequestData = {
        projectType,
        description,
        location,
        budget,
        timeframe,
        contactInfo: {
          ...contactInfo,
          billingAddress: contactInfo.sameAddress ? contactInfo.prestationAddress : contactInfo.billingAddress
        },
        images,
        type: "custom_request",
        createdAt: new Date().toISOString()
      };

      const response = await fetch("/api/custom-requests", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(customRequestData),
      });

      if (response.ok) {
        setIsSubmitted(true);
      } else {
        throw new Error("Erreur lors de l'envoi");
      }
    } catch (error) {
      console.error("Erreur:", error);
      setError("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="bg-white shadow-lg rounded-lg p-6 text-center">
        <FiCheckCircle className="text-green-500 text-6xl mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-green-600 mb-2">Demande envoyée !</h2>
        
        <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
          <h3 className="font-semibold text-gray-800 mb-3">Récapitulatif de votre demande :</h3>
          <div className="space-y-2">
            <p className="text-sm text-gray-700">
              <strong>Type de projet :</strong> {projectType || "Non spécifié"}
            </p>
            <p className="text-sm text-gray-700">
              <strong>Description :</strong> {description.substring(0, 100)}...
            </p>
            {location && (
              <p className="text-sm text-gray-700">
                <strong>Localisation :</strong> {location}
              </p>
            )}
            {timeframe && (
              <p className="text-sm text-gray-700">
                <strong>Planning souhaité :</strong> {
                  timeframe === 'urgent' ? '🚨 Urgent - sous 2h' :
                  timeframe === 'rapide' ? '⚡ Rapide - 24h' :
                  timeframe === 'cette-semaine' ? '📍 Cette semaine' :
                  timeframe === 'ce-mois' ? '📆 Ce mois-ci' :
                  timeframe === 'mois-prochain' ? '📅 Le mois prochain' :
                  timeframe === 'trimestre' ? '🗓️ Dans les 3 mois' :
                  timeframe === 'flexible' ? '⏰ Flexible' :
                  timeframe === 'devis-seulement' ? '📋 Devis seulement' :
                  'Non spécifié'
                }
              </p>
            )}
            {budget && (
              <p className="text-sm text-gray-700">
                <strong>Budget :</strong> {
                  budget === 'moins-300' ? 'Moins de 300€' :
                  budget === '300-800' ? '300€ - 800€' :
                  budget === '800-2000' ? '800€ - 2000€' :
                  budget === '2000-5000' ? '2000€ - 5000€' :
                  budget === '5000-10000' ? '5000€ - 10000€' :
                  budget === 'plus-10000' ? 'Plus de 10000€' :
                  budget === 'devis-gratuit' ? 'Devis gratuit demandé' :
                  'Non spécifié'
                }
              </p>
            )}
          </div>
        </div>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h4 className="font-semibold text-blue-800 mb-2">Prochaines étapes :</h4>
          <div className="text-sm text-blue-700">
            <p>📱 Vous serez recontacté par SMS ou appel téléphonique</p>
          </div>
        </div>
        
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-6">
          <p className="text-sm text-yellow-800">
            <strong>Besoin d'aide ?</strong> Contactez-nous au <strong>06 58 65 69 90</strong>
          </p>
        </div>
        
        <button
          onClick={() => {
            onClose();
            // Forcer le retour à l'accueil
            window.location.href = '/';
          }}
          className="bg-primary text-white py-3 px-8 rounded-md hover:bg-secondary transition-colors font-medium"
        >
          Retour à l'accueil
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-lg rounded-lg p-6">
      <h2 className="text-2xl font-bold text-secondary mb-6">Décrivez votre projet</h2>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* ÉTAPE 1: INFORMATIONS DE CONTACT */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-secondary mb-4">📞 Vos informations de contact</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom complet *
              </label>
              <input
                type="text"
                value={contactInfo.name}
                onChange={(e) => setContactInfo(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Votre nom et prénom"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Téléphone *
              </label>
              <input
                type="tel"
                value={contactInfo.phone}
                onChange={(e) => setContactInfo(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Votre numéro de téléphone"
                required
              />
            </div>
            
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={contactInfo.email}
                onChange={(e) => setContactInfo(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Votre adresse email (optionnel)"
              />
            </div>
            
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Adresse d'intervention *
              </label>
              <textarea
                value={contactInfo.prestationAddress}
                onChange={(e) => setContactInfo(prev => ({ ...prev, prestationAddress: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Adresse complète où les travaux doivent être réalisés"
                required
              />
            </div>
            
            <div className="md:col-span-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={contactInfo.sameAddress}
                  onChange={(e) => handleSameAddressChange(e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm">Adresse de facturation identique à l'adresse d'intervention</span>
              </label>
            </div>
            
            {!contactInfo.sameAddress && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Adresse de facturation
                </label>
                <textarea
                  value={contactInfo.billingAddress}
                  onChange={(e) => setContactInfo(prev => ({ ...prev, billingAddress: e.target.value }))}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Adresse complète pour la facturation"
                />
              </div>
            )}
          </div>
        </div>

        {/* ÉTAPE 2: VOTRE PROJET */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-secondary mb-4">🔧 Votre projet</h3>
          
          <div className="space-y-4">
            {/* Type de projet */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quel type de travaux souhaitez-vous ? *
              </label>
              <select
                value={projectType}
                onChange={(e) => setProjectType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                required
              >
                <option value="">Choisissez le type de travaux</option>
                <option value="renovation-salle-bain">Rénovation salle de bain</option>
                <option value="renovation-cuisine">Rénovation cuisine</option>
                <option value="installation-multiple">Plusieurs installations</option>
                <option value="reparations-multiples">Plusieurs réparations</option>
                <option value="urgence-depannage">Urgence / Dépannage</option>
                <option value="creation-salle-eau">Création nouvelle salle d'eau</option>
                <option value="maintenance-annuelle">Maintenance / Entretien</option>
                <option value="devis-gratuit">Demande de devis gratuit</option>
                <option value="autre">Autre projet</option>
              </select>
            </div>

            {/* Localisation dans le logement */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Où se situent les travaux ?
              </label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Exemple : Salle de bain étage, cuisine, WC rez-de-chaussée..."
              />
            </div>

            {/* Description détaillée */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Décrivez votre projet en détail *
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={5}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Exemple : Je souhaite refaire ma salle de bain de 6m². Remplacer la baignoire par une douche italienne, changer tous les robinets, installer un nouveau lavabo double vasque..."
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Plus vous donnez de détails, plus notre devis sera précis !
              </p>
            </div>
          </div>
        </div>

        {/* ÉTAPE 3: PLANNING ET BUDGET */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-secondary mb-4">📅 Planning et budget</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Délai souhaité */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quand aimeriez-vous que les travaux soient réalisés ?
              </label>
              <select
                value={timeframe}
                onChange={(e) => setTimeframe(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">Choisissez votre planning idéal</option>
                <option value="urgent">🚨 Urgent - J'ai une fuite ou panne (sous 2h)</option>
                <option value="rapide">⚡ Rapide - Dans les 24h</option>
                <option value="cette-semaine">📍 Cette semaine</option>
                <option value="ce-mois">📆 Ce mois-ci</option>
                <option value="mois-prochain">📅 Le mois prochain</option>
                <option value="trimestre">🗓️ Dans les 3 mois</option>
                <option value="flexible">⏰ Je suis flexible</option>
                <option value="devis-seulement">📋 Juste un devis pour l'instant</option>
              </select>
            </div>

            {/* Budget */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Avez-vous une idée de budget ?
              </label>
              <select
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">Quel est votre budget approximatif ?</option>
                <option value="moins-300">Moins de 300€ (petite réparation)</option>
                <option value="300-800">300€ - 800€ (réparation importante)</option>
                <option value="800-2000">800€ - 2000€ (installation simple)</option>
                <option value="2000-5000">2000€ - 5000€ (rénovation partielle)</option>
                <option value="5000-10000">5000€ - 10000€ (rénovation complète)</option>
                <option value="plus-10000">Plus de 10000€ (gros projet)</option>
                <option value="devis-gratuit">💰 Je veux un devis gratuit</option>
              </select>
            </div>
          </div>
        </div>

        {/* Photos */}
        <div>
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center justify-between w-full p-3 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors"
          >
            <span className="font-medium">Ajouter des photos (optionnel)</span>
            {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
          </button>
          
          {isExpanded && (
            <div className="mt-3 p-3 border border-gray-200 rounded-md">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                accept="image/*"
                multiple
                className="hidden"
              />
              
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-secondary transition-colors"
              >
                <FiImage className="mr-2" />
                Ajouter des photos
              </button>
              
              <p className="text-xs text-gray-500 mt-2">
                Ajoutez des photos pour nous aider à mieux comprendre votre projet. Max 5MB par image.
              </p>
              
              {images.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mt-3">
                  {images.map((image, index) => (
                    <div key={index} className="relative">
                      <img src={image} alt={`Photo ${index + 1}`} className="w-full h-20 object-cover rounded" />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 text-xs hover:bg-red-600"
                      >
                        <FiTrash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {error && (
          <div className="p-3 bg-red-100 text-red-700 rounded-md text-sm">
            {error}
          </div>
        )}

        <div className="flex gap-4">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-3 px-6 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
          >
            Annuler
          </button>
          
          <button
            type="submit"
            disabled={isLoading}
            className="flex-1 bg-primary text-white py-3 px-6 rounded-md hover:bg-secondary transition-colors disabled:opacity-50 flex items-center justify-center"
          >
            {isLoading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
            ) : (
              <>
                <FiSend className="mr-2" />
                Envoyer ma demande
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
} 